import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle, ArrowRight, Calendar, Phone, Play, 
  Baby, User, Headphones, Stethoscope, Heart, Clock,
  Shield, Award, Zap, Volume2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";

import VideoCard from "@/components/common/VideoCard";
import FAQSection from "@/components/common/FAQSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CI Journey Steps
const ciJourney = [
  {
    step: 1,
    title: "Initial Consultation",
    description: "Comprehensive hearing evaluation and candidacy assessment with Dr. Sachdeva.",
    icon: Stethoscope,
  },
  {
    step: 2,
    title: "Pre-Surgery Preparation",
    description: "Medical tests, imaging, and counseling to prepare for surgery.",
    icon: Shield,
  },
  {
    step: 3,
    title: "CI Surgery",
    description: "Minimally invasive procedure performed by our expert team, typically 2-3 hours.",
    icon: Zap,
  },
  {
    step: 4,
    title: "Recovery Period",
    description: "2-4 weeks healing period before activation of the implant.",
    icon: Clock,
  },
  {
    step: 5,
    title: "Activation Day",
    description: "The exciting moment when the implant is switched on and you hear for the first time.",
    icon: Volume2,
  },
  {
    step: 6,
    title: "Mapping & Rehab",
    description: "Ongoing programming adjustments and auditory rehabilitation for optimal outcomes.",
    icon: Heart,
  },
];

// Candidacy criteria
const adultCandidacy = [
  "Severe to profound sensorineural hearing loss in both ears",
  "Limited benefit from hearing aids (less than 50% speech recognition)",
  "No medical contraindications for surgery",
  "Realistic expectations and commitment to rehabilitation",
  "Strong motivation to participate in the hearing process",
];

const pediatricCandidacy = [
  "Profound bilateral sensorineural hearing loss",
  "Age 12 months or older (sometimes younger in special cases)",
  "Limited or no benefit from hearing aids after trial period",
  "Family commitment to auditory rehabilitation",
  "Access to educational and therapy support",
];

// CI vs Hearing Aids comparison
const comparison = [
  {
    feature: "How it works",
    ci: "Bypasses damaged hair cells, directly stimulates auditory nerve",
    hearingAid: "Amplifies sound for remaining hair cells to detect",
  },
  {
    feature: "Best for",
    ci: "Severe to profound hearing loss",
    hearingAid: "Mild to moderate hearing loss",
  },
  {
    feature: "Speech understanding",
    ci: "Can achieve excellent results even in severe loss",
    hearingAid: "Depends on residual hearing",
  },
  {
    feature: "Process",
    ci: "Requires surgery and rehabilitation",
    hearingAid: "Non-surgical, immediate use",
  },
  {
    feature: "Maintenance",
    ci: "External processor needs regular care, internal implant is permanent",
    hearingAid: "Regular cleaning, battery changes",
  },
];

export default function CochlearImplantPage() {
  const [videos, setVideos] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [videosRes, faqsRes] = await Promise.all([
          axios.get(`${API}/videos?category=cochlear_implant`),
          axios.get(`${API}/faqs?category=cochlear_implant`),
        ]);
        
        setVideos(videosRes.data);
        setFaqs(faqsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div data-testid="cochlear-implant-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-50 via-white to-sky-50 py-16 md:py-24">
        <div className="container-width">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-sky-100 px-4 py-2 rounded-full mb-6">
                <Award className="w-4 h-4 text-sky-600" />
                <span className="text-sm font-semibold text-sky-700">India's Leading CI Center</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Cochlear Implant<br />
                <span className="text-sky-600">Center of Excellence</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-xl leading-relaxed">
                With over <span className="font-semibold">1000+ successful cochlear implant surgeries</span>, 
                Dr. Sanjay Sachdeva offers world-class CI care with international training and 37+ years of expertise.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/book-appointment">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-14 px-8">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book CI Consultation
                  </Button>
                </Link>
                <a href="tel:+919910317286">
                  <Button size="lg" variant="outline" className="h-14 px-8">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1615463369266-9ec534b52538?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGNoaWxkJTIwd2l0aCUyMGNvY2hsZWFyJTIwaW1wbGFudCUyMHNtaWxpbmd8ZW58MHx8fHwxNzcyMTg2NTcwfDA&ixlib=rb-4.1.0&q=85"
                alt="Child with cochlear implant"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is CI Section */}
      <section className="section-padding bg-white" id="what-is-ci">
        <div className="container-width">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              What is a Cochlear Implant?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              A cochlear implant is a surgically implanted electronic device that provides a sense of sound 
              to a person who has severe to profound hearing loss. Unlike hearing aids that amplify sound, 
              cochlear implants bypass the damaged portions of the ear and directly stimulate the auditory nerve.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-slate-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">External Components</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5" />
                  <span className="text-slate-600">Microphone to capture sound</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5" />
                  <span className="text-slate-600">Sound processor to convert sound to digital signals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5" />
                  <span className="text-slate-600">Transmitter coil to send signals to internal implant</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Internal Components</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5" />
                  <span className="text-slate-600">Receiver-stimulator implanted under the skin</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5" />
                  <span className="text-slate-600">Electrode array inserted into the cochlea</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5" />
                  <span className="text-slate-600">Direct stimulation of the auditory nerve</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Link to Products */}
          <div className="mt-12 text-center">
            <Link to="/cochlear-implant/products">
              <Button size="lg" className="bg-sky-600 hover:bg-sky-700">
                View CI Products & Pricing
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Candidacy Section */}
      <section className="section-padding bg-slate-50" id="candidacy">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Who is a Candidate for CI?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Cochlear implants can help both adults and children with severe to profound hearing loss.
            </p>
          </div>

          <Tabs defaultValue="adults" className="max-w-4xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="adults" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Adults
              </TabsTrigger>
              <TabsTrigger value="children" className="flex items-center gap-2">
                <Baby className="w-4 h-4" />
                Children
              </TabsTrigger>
            </TabsList>

            <TabsContent value="adults">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Adult CI Candidacy Criteria</h3>
                <ul className="space-y-4">
                  {adultCandidacy.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/book-appointment">
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      Check Your Candidacy
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="children">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Pediatric CI Candidacy Criteria</h3>
                <ul className="space-y-4">
                  {pediatricCandidacy.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 p-4 bg-sky-50 rounded-lg">
                  <p className="text-sm text-sky-800">
                    <strong>Early implantation is crucial:</strong> Children who receive cochlear implants before 
                    age 3 typically develop speech and language skills comparable to their hearing peers.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CI Journey Timeline */}
      <section className="section-padding bg-white" id="surgery">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The Cochlear Implant Journey
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From consultation to hearing, here's what to expect during your CI journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {ciJourney.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative bg-slate-50 rounded-xl p-6"
              >
                <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold">
                  {item.step}
                </div>
                <div className="pt-4">
                  <item.icon className="w-8 h-8 text-sky-600 mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hearing Aids vs CI */}
      <section className="section-padding bg-slate-50" id="comparison">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Hearing Aids vs Cochlear Implants
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Understanding the difference helps determine the right solution for your hearing loss.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-3 bg-slate-900 text-white p-4">
              <div className="font-semibold">Feature</div>
              <div className="font-semibold text-sky-400">Cochlear Implant</div>
              <div className="font-semibold">Hearing Aid</div>
            </div>
            {comparison.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 p-4 ${i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                <div className="font-medium text-slate-900">{row.feature}</div>
                <div className="text-slate-600 text-sm">{row.ci}</div>
                <div className="text-slate-600 text-sm">{row.hearingAid}</div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-600 mb-4">Not sure which is right for you?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book-appointment">
                <Button className="bg-amber-600 hover:bg-amber-700">
                  Get Expert Guidance
                </Button>
              </Link>
              <Link to="/hearing-aids">
                <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                  Browse Hearing Aids
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Video Gallery */}
      <section className="section-padding bg-white">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              CI Video Library
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Learn more about cochlear implants from Dr. Sachdeva's educational videos.
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-video bg-slate-200 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/videos">
              <Button variant="outline" size="lg">
                <Play className="w-5 h-5 mr-2" />
                View All Videos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      {faqs.length > 0 && (
        <div id="faqs">
          <FAQSection faqs={faqs} title="Cochlear Implant FAQs" />
        </div>
      )}

      {/* CTA Section */}
      <section className="bg-sky-600 py-16 md:py-24">
        <div className="container-width text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Take the First Step Towards Better Hearing
          </h2>
          <p className="text-lg text-sky-100 max-w-2xl mx-auto mb-8">
            Book a consultation to discuss if cochlear implant is right for you or your loved one.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-appointment">
              <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 font-bold h-14 px-8">
                <Calendar className="w-5 h-5 mr-2" />
                Book Consultation
              </Button>
            </Link>
            <a href="tel:+919910317286">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9910317286
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
